@if (question.kind === 'CONFIRMATION' && question.plan?.length) {
  <ol class="assistant-plan" aria-label="Planned change">
    @for (step of question.plan; track $index) {
      <li>
        <span class="assistant-plan-action" [attr.data-action]="step.action">{{ words(step.action) }}</span>
        <span class="assistant-plan-kind">{{ words(step.kind) }}</span>
        <strong>{{ step.name }}</strong>
        @if (step.id) {
          <span class="assistant-plan-id">{{ step.id }}</span>
        }
        @if (step.changes.length) {
          <dl class="assistant-plan-changes">
            @for (change of step.changes; track $index) {
              <dt>{{ change.field }}</dt>
              <dd>
                @if (change.before) {
                  <del>{{ change.before }}</del>
                }
                @if (change.before && change.after) {
                  <span aria-hidden="true">→</span>
                }
                @if (change.after) {
                  <ins>{{ change.after }}</ins>
                }
              </dd>
            }
          </dl>
        }
      </li>
    }
  </ol>
}

@if (timeRangeInput; as input) {
  <div class="assistant-options" role="group" [attr.aria-label]="question.question">
    @for (preset of input.presets; track $index) {
      <button type="button" class="assistant-option" [class.primary]="$first" (click)="onPreset(preset)">
        {{ preset.label }}
      </button>
    }
    <button type="button" class="assistant-option" [attr.aria-expanded]="!!customRange" (click)="onCustomRange(input.presets)">
      Custom range…
    </button>
  </div>
  @if (customRange) {
    <form class="assistant-form" (ngSubmit)="onSendCustomRange()">
      <label>
        From
        <input type="datetime-local" name="from" [(ngModel)]="customRange.from" required />
      </label>
      <label>
        To
        <input type="datetime-local" name="to" [(ngModel)]="customRange.to" required />
      </label>
      <button type="submit" class="assistant-option primary" [disabled]="!customAnswer()">Use this range</button>
    </form>
  }
} @else if (resourceInput; as input) {
  <div class="assistant-picker">
    @if (input.candidates.length > 6) {
      <input type="search" class="assistant-field" name="filter" placeholder="Filter…" aria-label="Filter" [(ngModel)]="filter" />
    }
    <ul role="listbox" [attr.aria-label]="question.question" [attr.aria-multiselectable]="input.multiple">
      @for (candidate of visibleCandidates(input); track candidate.id) {
        <li>
          <button
            type="button"
            class="assistant-candidate"
            role="option"
            [attr.aria-selected]="selected.has(candidate.id)"
            [class.selected]="selected.has(candidate.id)"
            (click)="onCandidate(input, candidate)"
          >
            @if (input.multiple) {
              <i class="pi assistant-candidate-mark" [class.pi-check-square]="selected.has(candidate.id)" [class.pi-stop]="!selected.has(candidate.id)" aria-hidden="true"></i>
            } @else {
              <i class="pi pi-circle assistant-candidate-mark" aria-hidden="true"></i>
            }
            <span class="assistant-candidate-text">
              <span class="assistant-candidate-name">{{ candidate.name }}</span>
              @if (candidate.detail) {
                <span class="assistant-candidate-detail">{{ candidate.detail }}</span>
              }
            </span>
            @if (!input.multiple) {
              <i class="pi pi-chevron-right assistant-candidate-go" aria-hidden="true"></i>
            }
          </button>
        </li>
      } @empty {
        <li class="assistant-picker-empty">Nothing matches the filter.</li>
      }
    </ul>
    @if (input.multiple) {
      <button type="button" class="assistant-option primary" [disabled]="selected.size === 0" (click)="onSendCandidates(input)">
        Use {{ selected.size }} selected
      </button>
    }
  </div>
} @else if (durationInput; as input) {
  <div class="assistant-options" role="group" [attr.aria-label]="question.question">
    @for (preset of input.presets; track $index) {
      <button type="button" class="assistant-option" [class.primary]="$first" (click)="onDurationPreset(preset)">
        {{ preset.label }}
      </button>
    }
    <button type="button" class="assistant-option" [attr.aria-expanded]="!!customDuration" (click)="onCustomDuration()">Other length…</button>
  </div>
  @if (customDuration) {
    <form class="assistant-form" (ngSubmit)="onSendCustomDuration(input)">
      <label>
        Length
        <input type="number" min="1" step="1" name="amount" class="assistant-field" [(ngModel)]="customDuration.amount" required />
      </label>
      <label>
        Unit
        <select name="unit" class="assistant-field" [(ngModel)]="customDuration.unit">
          <option value="M">minutes</option>
          <option value="H">hours</option>
          <option value="D">days</option>
        </select>
      </label>
      <button type="submit" class="assistant-option primary" [disabled]="!customDurationValue(input)">Use this length</button>
    </form>
  }
} @else if (numberInput; as input) {
  <form class="assistant-form" (ngSubmit)="onSendNumber(input)">
    <label>
      Value
      <span class="assistant-field-with-unit">
        <input
          type="number"
          name="value"
          class="assistant-field"
          [attr.min]="input.min"
          [attr.max]="input.max"
          [attr.step]="input.integer ? 1 : 'any'"
          [(ngModel)]="numberValue"
          required
        />
        @if (input.unit) {
          <span>{{ input.unit }}</span>
        }
      </span>
    </label>
    <button type="submit" class="assistant-option primary" [disabled]="!numberValid(input)">Use this value</button>
  </form>
} @else if (attributesInput; as input) {
  <div class="assistant-attributes">
    <ul class="assistant-options" [attr.aria-label]="input.mode === 'ROLES' ? 'Roles' : 'Attributes'">
      @for (attribute of attributes; track attribute) {
        <li class="assistant-tag">
          {{ attribute }}
          <button type="button" [attr.aria-label]="'Remove ' + attribute" (click)="onRemoveAttribute(attribute)"><i class="pi pi-times"></i></button>
        </li>
      }
    </ul>
    <div class="assistant-form">
      <input
        type="text"
        name="attribute"
        class="assistant-field"
        [placeholder]="input.mode === 'ROLES' ? '#attribute' : 'attribute'"
        [attr.aria-invalid]="!!attributeDraft.trim() && !attributeDraftValid(input)"
        [(ngModel)]="attributeDraft"
        (keydown)="onAttributeKeydown($event, input)"
      />
      <button type="button" class="assistant-option" [disabled]="!attributeDraftValid(input)" (click)="onAddAttribute(input)">Add</button>
      <button type="button" class="assistant-option primary" (click)="onSendAttributes(input)">Use these</button>
    </div>
    @if (attributeDraft.trim() && !attributeDraftValid(input)) {
      <p class="assistant-field-error">
        {{ input.mode === 'ROLES' ? 'Start a role with # for an attribute or @ for an id.' : 'Use letters, digits and . _ : / - only, with no # or @.' }}
      </p>
    }
  </div>
} @else if (textInput; as input) {
  <form class="assistant-form" (ngSubmit)="onSendText(input)">
    <input
      type="text"
      name="text"
      class="assistant-field"
      [placeholder]="placeholders[input.format]"
      [attr.aria-label]="question.question"
      [attr.aria-invalid]="!!textValue.trim() && !textValid(input)"
      [(ngModel)]="textValue"
    />
    <button type="submit" class="assistant-option primary" [disabled]="!textValid(input)">Use this value</button>
  </form>
  @if (textValue.trim() && !textValid(input)) {
    <p class="assistant-field-error">That is not a valid {{ words(input.format).toLowerCase() }}.</p>
  }
} @else if (question.options.length) {
  @if (discussing) {
    <form class="assistant-form" (ngSubmit)="onSendDiscussion()">
      <textarea
        #discussionField
        name="discussion"
        class="assistant-field assistant-discussion"
        rows="2"
        placeholder="What would you like to change or ask?"
        aria-label="Your reply about the plan"
        [(ngModel)]="discussion"
        (keydown)="onDiscussionKeydown($event)"
      ></textarea>
      <button type="submit" class="assistant-option primary" [disabled]="!discussion.trim()">Send</button>
      <button type="button" class="assistant-text-button" (click)="onEndDiscussion()">Back</button>
    </form>
  } @else {
    <div class="assistant-options" role="group" [attr.aria-label]="question.question">
      @for (option of question.options; track $index) {
        <button type="button" class="assistant-option" [class.primary]="question.kind === 'CONFIRMATION' && $first" (click)="onOption(option, $first)">
          {{ option }}
        </button>
      }
      @if (question.kind === 'CONFIRMATION') {
        <button #discussButton type="button" class="assistant-option" (click)="onDiscuss()">Let's chat about this</button>
      }
    </div>
  }
}
